import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
    PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneCall,
    Loader2, User, UserCheck, ChevronRight, Clock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDuration(s) {
    if (!s || s <= 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
}
function fmtDateTime(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } catch { return '—'; }
}

export default function CallHistoryModal({ open, onOpenChange, number }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!open || !number) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}/api/calls/history?number=${encodeURIComponent(number)}&limit=200`, { withCredentials: true });
                if (!cancelled) setData(res.data);
            } catch {
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, number]);

    const stats = data?.stats || {};
    const match = data?.match;
    const calls = data?.calls || [];
    const totalDur = (() => {
        const s = stats.total_seconds || 0;
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        return h > 0 ? `${h}h ${m}min` : `${m}min`;
    })();

    const goToContact = () => {
        if (!match) return;
        onOpenChange(false);
        if (match.type === 'client' && match.id) navigate(`/clients/${match.id}`);
        else if (match.type === 'prospect' && match.id) navigate(`/prospects/${match.id}`);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="call-history-modal">
                <DialogHeader className="px-6 pt-6 pb-3 bg-gradient-to-r from-[#1B365D] to-[#2A4A7A]">
                    <DialogTitle className="text-white text-lg font-bold flex items-center gap-2">
                        <PhoneCall className="w-5 h-5 text-[#C9A962]" />
                        Historique d'appel
                    </DialogTitle>
                    <p className="text-white/80 text-sm font-mono mt-1">{number}</p>
                </DialogHeader>

                {/* Match card */}
                {match && (
                    <button
                        onClick={goToContact}
                        className="w-full px-6 py-3 bg-[#C9A962]/10 border-b border-[#C9A962]/20 flex items-center justify-between hover:bg-[#C9A962]/20 transition-colors group"
                        data-testid="call-history-open-contact"
                    >
                        <div className="flex items-center gap-2.5">
                            {match.type === 'client'
                                ? <UserCheck className="w-4 h-4 text-emerald-500" />
                                : <User className="w-4 h-4 text-purple-500" />}
                            <div className="text-left">
                                <p className="text-sm font-bold text-[#1B365D]">{match.nom}</p>
                                <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                                    {match.type === 'client' ? 'Client' : `Prospect${match.statut ? ` · ${match.statut.replace(/_/g, ' ')}` : ''}`}
                                </p>
                            </div>
                        </div>
                        <span className="text-xs text-[#C9A962] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                            Ouvrir la fiche <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                    </button>
                )}

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 px-6 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="text-center">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">Total</p>
                        <p className="text-lg font-bold text-[#1B365D]">{stats.total || 0}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">Entrants</p>
                        <p className="text-lg font-bold text-emerald-600">{stats.incoming || 0}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">Sortants</p>
                        <p className="text-lg font-bold text-blue-600">{stats.outgoing || 0}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-[10px] uppercase tracking-wide text-red-500 font-semibold">Manqués</p>
                        <p className="text-lg font-bold text-red-500">{stats.missed || 0}</p>
                    </div>
                </div>

                {/* Footer info */}
                <div className="px-6 py-2 bg-white border-b border-gray-100 text-xs text-gray-500 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    Durée totale : <span className="font-semibold text-[#1B365D]">{totalDur}</span>
                </div>

                {/* List */}
                <div className="max-h-[55vh] overflow-y-auto" data-testid="call-history-list">
                    {loading && (
                        <div className="px-6 py-12 flex items-center justify-center text-gray-400 gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                        </div>
                    )}
                    {!loading && calls.length === 0 && (
                        <div className="px-6 py-12 text-center text-sm text-gray-400">
                            Aucun appel trouvé pour ce numéro.
                        </div>
                    )}
                    {!loading && calls.map(c => {
                        const Icon = c.missed ? PhoneMissed : (c.direction === 'outgoing' ? PhoneOutgoing : PhoneIncoming);
                        const color = c.missed ? 'text-red-500' : (c.direction === 'outgoing' ? 'text-blue-500' : 'text-emerald-500');
                        const bg = c.missed ? 'bg-red-50' : (c.direction === 'outgoing' ? 'bg-blue-50' : 'bg-emerald-50');
                        const label = c.missed ? 'Manqué' : (c.direction === 'outgoing' ? 'Sortant' : 'Entrant');
                        return (
                            <div key={c.cdr_id} className={`px-6 py-3 border-b border-gray-50 flex items-center gap-3 ${c.missed ? 'bg-red-50/30' : ''}`} data-testid={`history-row-${c.cdr_id}`}>
                                <div className={`shrink-0 w-9 h-9 rounded-full ${bg} flex items-center justify-center`}>
                                    <Icon className={`w-4 h-4 ${color}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-[#1B365D]">{label}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {fmtDateTime(c.start_datetime)} · ligne {c.phone_line || '—'}
                                    </p>
                                </div>
                                <div className="text-sm font-mono text-right">
                                    {c.missed
                                        ? <span className="text-red-500 font-semibold">Manqué</span>
                                        : <span className="text-gray-700">{fmtDuration(c.duration_seconds)}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}
